/**
 * ClusterTopNav — shared navigation bar for both Token Intelligence and Wallet Intelligence.
 * Mode toggle immediately switches between dashboards.
 */
import { ArrowLeft, CircleDot, Diamond, Menu, Route, Search } from 'lucide-react';
import type { Dataset, ScanContext } from '@/data/clusterscope-data';

interface ClusterTopNavProps {
  dataset: Dataset;
  mode: ScanContext['mode'];
  onModeChange: (mode: ScanContext['mode']) => void;
  searchValue: string;
  onSearchChange: (v: string) => void;
  onSearchSubmit: () => void;
  onNewSearch: () => void;
  onToggleSidebar?: () => void;
  isAnalyzing?: boolean;
  showCapitalTrace?: boolean;
  onToggleCapitalTrace?: () => void;
}

export default function ClusterTopNav({
  dataset,
  mode,
  onModeChange,
  searchValue,
  onSearchChange,
  onSearchSubmit,
  onNewSearch,
  onToggleSidebar,
  isAnalyzing,
  showCapitalTrace,
  onToggleCapitalTrace,
}: ClusterTopNavProps) {
  return (
    <div className="relative z-30 flex flex-wrap items-center gap-3 border-b border-white/[0.08] bg-background/80 px-4 py-2.5 backdrop-blur-xl">
      {onToggleSidebar && (
        <button
          onClick={onToggleSidebar}
          className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 text-muted-foreground transition-colors hover:bg-white/10 hover:text-foreground lg:hidden"
        >
          <Menu className="h-4 w-4" />
        </button>
      )}

      <div className="flex items-center gap-2 pr-2">
        <span className="h-2 w-2 rounded-full bg-primary shadow-[0_0_8px_2px_hsl(var(--primary)/0.7)]" />
        <span className="text-sm font-extrabold tracking-tight text-foreground">ClusterScope</span>
      </div>

      <div className="flex items-center rounded-full border border-white/10 bg-white/[0.03] p-0.5 text-xs">
        <button
          onClick={() => onModeChange('token')}
          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 font-semibold transition-colors ${
            mode === 'token' ? 'bg-white/10 text-foreground' : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <CircleDot className="h-3.5 w-3.5" />
          Token
        </button>
        <button
          onClick={() => onModeChange('wallet')}
          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 font-semibold transition-colors ${
            mode === 'wallet' ? 'bg-white/10 text-foreground' : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <Diamond className="h-3.5 w-3.5" />
          Wallet
        </button>
      </div>

      <div className="relative order-last min-w-[160px] flex-1 basis-full sm:order-none sm:basis-56 md:basis-72">
        <div className="flex w-full items-stretch gap-1.5">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground/60" />
            <input
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && onSearchSubmit()}
              placeholder="New search..."
              disabled={isAnalyzing}
              className="w-full rounded-full border border-white/10 bg-white/[0.03] py-1.5 pl-8 pr-3 text-xs text-foreground placeholder:text-muted-foreground/50 outline-none transition-colors focus:border-primary/40 disabled:opacity-50"
            />
          </div>
          <button
            onClick={onSearchSubmit}
            disabled={isAnalyzing || !searchValue.trim()}
            className="inline-flex items-center rounded-full bg-primary px-3 py-1 text-xs font-bold text-primary-foreground transition-all hover:opacity-90 disabled:opacity-40"
          >
            Analyze
          </button>
        </div>
      </div>

      <div className="ml-auto flex items-center gap-2 text-xs">
        {mode === 'token' && (
          <span className="hidden items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-1.5 font-mono sm:inline-flex">
            <span className="font-bold text-foreground">
              {dataset.token.name && dataset.token.name !== dataset.token.symbol
                ? `${dataset.token.name} (${dataset.token.symbol})`
                : dataset.token.symbol}
            </span>
            <span className="text-muted-foreground">{dataset.token.price}</span>
            <span className="rounded-md bg-primary/15 px-1.5 py-0.5 font-semibold text-primary">
              {dataset.token.change}
            </span>
          </span>
        )}

        {mode === 'token' && onToggleCapitalTrace && (
          <button
            onClick={onToggleCapitalTrace}
            className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-bold transition-all ${
              showCapitalTrace
                ? 'border-[#a78bfa]/50 bg-[#a78bfa]/25 text-[#a78bfa]'
                : 'border-[#a78bfa]/30 bg-[#a78bfa]/10 text-[#a78bfa] hover:bg-[#a78bfa]/20'
            }`}
          >
            <Route className="h-3.5 w-3.5" />
            Trace Capital
          </button>
        )}

        <button
          onClick={onNewSearch}
          className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-white/10 px-3.5 font-semibold text-muted-foreground transition-all duration-200 hover:scale-[1.02] active:scale-95 hover:border-white/20 hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">New Search</span>
        </button>
      </div>
    </div>
  );
}
